/**
 * Helpers to convert SKU quantities across Megaventory product units while preserving extended value:
 * qty_to = qty_from * factor (target units per one source unit), unit_cost_to = extended / qty_to.
 */

function collapseWhitespace(lower: string): string {
  return lower.replace(/\s+/g, " ").trim();
}

/** Normalize MV / human unit labels for matching. */
export function normalizeUnitLabel(raw: string | undefined | null): string {
  if (raw == null) return "";
  return collapseWhitespace(String(raw).toLowerCase());
}

/**
 * Lowercase, collapse whitespace, and strip parenthetical suffixes Megaventory uses on labels
 * (for example "Pound(s)" → "pound") before mass / count matching.
 */
export function canonicalUnitForMatching(raw: string | undefined | null): string {
  if (raw == null) return "";
  const lower = normalizeUnitLabel(raw);
  return collapseWhitespace(lower.replace(/\([^)]*\)/g, " "));
}

/** Mass in grams for exactly one canonical unit of `label`, or null when not a known mass unit. */
function gramsPerUnit(label: string): number | null {
  const u = canonicalUnitForMatching(label);
  if (u === "") return null;
  if (u === "g" || u === "gram" || u === "grams") return 1;
  if (u === "kg" || u === "kilogram" || u === "kilograms" || u === "kgs") return 1000;
  if (u === "lb" || u === "lbs" || u === "pound" || u === "pounds" || u === "#") return 453.59237;
  if (u === "oz" || u === "ounce" || u === "ounces") return 28.349523125;
  if (u === "mg" || u === "milligram" || u === "milligrams") return 0.001;
  return null;
}

function isCountLike(label: string): boolean {
  const u = canonicalUnitForMatching(label);
  return (
    u === "ea" ||
    u === "each" ||
    u === "pc" ||
    u === "pcs" ||
    u === "piece" ||
    u === "pieces" ||
    u === "unit" ||
    u === "units"
  );
}

/**
 * Conversion factor **F** such that qty_in_target_units = qty_in_source_units * F.
 * Returns null when units differ and neither mass nor compatible count semantics apply.
 *
 * Compatible count semantics: both sides count-like ⇒ F = 1.
 */
export function conversionFactorTargetPerSource(fromUom: string | undefined, toUom: string | undefined): number | null {
  const a = canonicalUnitForMatching(fromUom);
  const b = canonicalUnitForMatching(toUom);
  if (a === "" || b === "") return 1;
  if (a === b) return 1;
  const gA = gramsPerUnit(a);
  const gB = gramsPerUnit(b);
  if (gA != null && gB != null) {
    return gA / gB;
  }
  if (isCountLike(a) && isCountLike(b)) {
    return 1;
  }
  return null;
}

export function coercePositiveNumber(value: unknown): number | null {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}
